import { defineStore } from 'pinia';
import { usePlayerProfileStore } from '~/stores/player-profile';
import { CrmLevelSyncError } from '~/adapters/api-sync-crm-level/apostatudo-api-post-sync-crm-level.adapter';
import { resolveAdapter } from '~/adapters/registry';
import { normalizeCrmLevelSlug } from '~/utils/apostatudo/crm-level-slug';
import { vipOrderFromTag } from '~/utils/apostatudo/player-vip';
import { levelTiers, tierIndexOfLevel } from '~/utils/levels';

// Denúncia de divergência de nível CRM (Smartico × core).
//
// O core guarda um espelho da escada da Smartico e o expõe no /players/me
// (`crmLevel`); o widget da Smartico reporta o nível dele pela própria store
// (`currentUserLevelData`). Quando os dois discordam, o front AVISA o core:
// POST /crm/users/sync-level, com o Bearer do JOGADOR.
//
// Avisa, e só. A rota não corrige mais nada — ela enfileira o id pra revisão
// manual no backoffice (/crm/sync-divergences) e nenhum status significa "o
// nível foi corrigido". Depois da chamada o jogador continua exatamente como
// estava, e é assim que tem que ser.
//
// Consequência de desenho, e a regra que rege este arquivo inteiro: esta store
// é uma SAÍDA, não uma fonte de dado. Ela NÃO escreve no perfil, NÃO invalida
// cache de ninguém, NÃO abre/fecha nada e NÃO tem estado que alguma tela espere.
// Nenhum caminho — sucesso, recusa, timeout, Smartico fora do ar — pode
// atrasar ou travar o que o jogador está fazendo.
//
// O que ela garante, além da chamada em si:
//
// - No máximo UMA chamada por detecção de divergência (`attemptedSignature`):
//   quem observa são watchers, e watcher re-dispara — o freio mora aqui, não
//   em quem chama.
// - Denunciado é denunciado (`reportedUserId`): `reported`/`already_reported`
//   põem o jogador numa fila que SÓ sai por ação manual. Chamar de novo na
//   mesma sessão não muda nada, nem que ele suba de nível com a aba aberta.
// - Nenhuma recusa retenta: 404 (Smartico não conhece o jogador), 429 (rate
//   limit da rota) e 503 (orçamento do warehouse esgotado). Só 401 é
//   transitório (token ainda não pronto) e libera a assinatura.
// - Core sem registro (`crmLevel === null`) NÃO é divergência: chamar aí seria
//   a chamada preventiva a cada login que o contrato proíbe — e `unchanged`
//   também custa uma leitura cobrada do warehouse.
// - Smartico fora do ar não vira chamada: sem `currentUserLevelData` não há
//   `smarticoSlug`, e sem os DOIS lados não há divergência, só "não sei".
// - VIP não compara nada, SEJA DE ONDE VIER o sinal: `vip` do core no
//   /players/me, degrau atual da Smartico num elo VIP da régua (posição 30+,
//   o critério do resto do app) ou o `level` do core lendo como VIP. Decisão de
//   produto: nível VIP não se denuncia — pra ele a escada local é a autoridade.
export const useCrmLevelSyncStore = defineStore('crm-level-sync', () => {
  const playerProfileStore = usePlayerProfileStore();
  const { crmLevel, vip, isProfileKnown } = storeToRefs(playerProfileStore);

  const { currentUserLevelData } = storeToRefs(useSmarticoUserInfos());

  // Nível do jogador lido pela POSIÇÃO na régua da Smartico — a mesma leitura
  // da página de níveis e do LevelUpWatcher.
  const { level: smarticoLevel } = useSmarticoLevel();

  // Resolvido uma vez no setup (precisa do contexto Nuxt pelo CDN das artes).
  const tiers = levelTiers();

  const { userId } = useUserData();

  // Assinatura "userId:coreSlug×smarticoSlug" da última divergência já tratada
  // (com sucesso OU recusa terminal). É por DETECÇÃO, não um booleano de
  // sessão: se o par de slugs mudar de novo (o jogador subiu de nível com a
  // aba aberta), é uma divergência nova.
  const attemptedSignature = ref<string | null>(null);

  // Jogador já enfileirado pra revisão manual: enquanto ninguém resolver no
  // backoffice, qualquer chamada nova devolveria o mesmo `already_reported`.
  // Cala a sessão inteira, acima da assinatura por par de slugs.
  const reportedUserId = ref<number | null>(null);

  // Jogador confirmado como VIP pinado: divergência permanente por design.
  // Rede de segurança — a guarda de VIP abaixo já evita a chamada.
  const pinnedUserId = ref<number | null>(null);

  const inFlight = ref(false);

  /** O que a Smartico reporta, na mesma língua do slug do core. */
  const smarticoSlug = computed(() =>
    normalizeCrmLevelSlug(currentUserLevelData.value?.name),
  );

  /**
   * A Smartico diz VIP: o degrau atual cai num elo `isVip` da tabela de elos
   * (posição 30+ na régua). É EXATAMENTE o critério do resto do app (página de
   * níveis, pódio, celebração de elo, LevelCard do Clube) — a guarda concorda
   * com o que o jogador vê na tela, e sobrevive a renomeação dos degraus.
   *
   * De propósito NÃO lê as `core_public_tags` nem parseia o nome do degrau: a
   * tag não reflete o VIP com confiança, e o nome é cosmético.
   */
  const isSmarticoVip = computed(
    () => !!tiers[tierIndexOfLevel(smarticoLevel.value)]?.isVip,
  );

  /**
   * O core diz VIP fora do campo `vip`: o próprio `level` guardado lê como VIP
   * ('VIP 4', 'SIGNATURE'). O payload contraditório `vip: null` + `level:
   * "VIP 4"` já aconteceu; lá não há posição de régua, então o texto é o que
   * resta.
   */
  const isCoreLevelVip = computed(() => {
    const text = crmLevel.value?.split(' - ')[0];

    return !!text && vipOrderFromTag(text) !== null;
  });

  const isVipAnywhere = computed(
    () => isSmarticoVip.value || isCoreLevelVip.value,
  );

  /** O que o core guarda, normalizado pelo mesmo funil. */
  const storedSlug = computed(() => normalizeCrmLevelSlug(crmLevel.value));

  /**
   * Só há divergência quando os DOIS lados são conhecidos e discordam. Lado
   * ausente é "ainda não sei" (Smartico fora do ar, SDK ainda carregando, core
   * sem registro), e "não sei" não vira chamada ao warehouse.
   * VIP em QUALQUER dos lados nunca diverge.
   */
  const isDiverged = computed(
    () =>
      !!userId.value &&
      isProfileKnown.value &&
      vip.value === null &&
      !isVipAnywhere.value &&
      !!storedSlug.value &&
      !!smarticoSlug.value &&
      storedSlug.value !== smarticoSlug.value,
  );

  /**
   * Denuncia a divergência SE (e só se) há uma ainda não tratada.
   *
   * Idempotente por construção: pode ser chamada de watcher sem medo — quem já
   * foi tratado, está em vôo ou não diverge sai de graça. Não devolve nada e
   * não lança: quem chama não espera o resultado, porque não existe resultado
   * que mude a tela.
   */
  async function syncIfDiverged() {
    const id = userId.value;

    if (!id || !isDiverged.value || inFlight.value) return;
    if (reportedUserId.value === id || pinnedUserId.value === id) return;

    const signature = `${id}:${storedSlug.value}×${smarticoSlug.value}`;

    if (attemptedSignature.value === signature) return;

    // Marca ANTES de chamar: a detecção está tratada mesmo se a resposta
    // falhar — nenhum caminho de erro (exceto 401, abaixo) admite retentar.
    const previousSignature = attemptedSignature.value;

    attemptedSignature.value = signature;
    inFlight.value = true;

    try {
      const result = await resolveAdapter('apiSyncCrmLevel').handle({
        userId: id,
      });

      // A conta pode ter trocado enquanto a resposta vinha.
      if (userId.value !== id) return;

      // Daqui pra baixo NADA toca o perfil, o nível ou qualquer store de tela:
      // o `status` só decide se vale chamar de novo nesta sessão.
      //
      // - `reported` / `already_reported`: está na fila do backoffice, que só
      //   sai por ação manual. Cala a sessão pra este jogador.
      // - `vip_pinned`: divergência permanente por design (não deveria chegar
      //   aqui — a guarda de VIP vem antes).
      // - `unchanged`: os dois lados concordam pro core. O `crmLevel` que temos
      //   é que está velho, e ele NÃO é corrigido daqui — o próximo /players/me
      //   (foco, hub) traz o valor certo. A assinatura já queimou este par, e
      //   sem escrita reativa não há watcher pra redisparar.
      // - `recently_checked` / `unknown`: nada a fazer.
      if (
        result.status === 'reported' ||
        result.status === 'already_reported'
      ) {
        reportedUserId.value = id;
      }

      if (result.status === 'vip_pinned') {
        pinnedUserId.value = id;
      }
    } catch (err) {
      if (err instanceof CrmLevelSyncError) {
        // 401 não é recusa de negócio: é token do jogador expirado/ainda não
        // pronto (estado transitório). Diferente de 404/429/503, ele pode
        // resolver sozinho quando o header voltar válido — então liberamos a
        // assinatura pra retentar esse par, em vez de queimá-lo na sessão.
        if (err.kind === 'unauthenticated') {
          attemptedSignature.value = previousSignature;

          return;
        }

        // 503 é desistência silenciosa por contrato; o resto é logado pra
        // triagem. Nenhum deles é erro DO JOGADOR: a tela não muda, ele não vê
        // nada e continua com o nível que já tinha em mãos.
        if (err.kind !== 'budget_exhausted') {
          console.error(
            `[crm-level-sync] denúncia recusada (${err.kind}):`,
            err.message,
          );
        }
      } else {
        console.error('[crm-level-sync] falha inesperada:', err);
      }
    } finally {
      inFlight.value = false;
    }
  }

  /** Troca de conta / logout: a divergência tratada era daquele jogador. */
  function reset() {
    attemptedSignature.value = null;
    reportedUserId.value = null;
    pinnedUserId.value = null;
    inFlight.value = false;
  }

  return {
    isDiverged,
    syncIfDiverged,
    reset,
  };
});
